import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc,
  query,
  where,
  updateDoc,
  addDoc
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  deleteUser, 
  getAuth
} from 'firebase/auth';
import { db } from '../firebase';

interface User {
  uid: string;
  email: string;
  role: UserRole;
  displayName?: string;
  createdAt: string;
  isSuperAdmin?: boolean;
  subscription?: string;
  subscriptionExpiry?: string;
}

const Users: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('All');
  
  // Form state for adding a new user
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    role: 'student' as UserRole,
    subscription: 'basic'
  });
  
  // Form state for editing a user
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUser, setEditUser] = useState({
    displayName: '',
    role: '' as UserRole,
    subscription: ''
  });
  
  // Subscription options
  const subscriptionOptions = [
    { id: 'basic', name: t('users.subscriptions.basic'), features: [t('users.subscriptions.basicFeature1'), t('users.subscriptions.basicFeature2')] },
    { id: 'standard', name: t('users.subscriptions.standard'), features: [t('users.subscriptions.standardFeature1'), t('users.subscriptions.standardFeature2'), t('users.subscriptions.standardFeature3')] },
    { id: 'premium', name: t('users.subscriptions.premium'), features: [t('users.subscriptions.premiumFeature1'), t('users.subscriptions.premiumFeature2'), t('users.subscriptions.premiumFeature3'), t('users.subscriptions.premiumFeature4')] }
  ];

  // Fetch users from Firestore
  useEffect(() => {
    const fetchUsers = async () => {
      if (userData?.role !== 'admin') {
        setError(t('errors.noPermission'));
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        
        const fetchedUsers: User[] = snapshot.docs.map(doc => ({
          ...doc.data(),
          uid: doc.id,
        } as User));
        
        // Sort by role, then by display name or email
        const sortedUsers = fetchedUsers.sort((a, b) => {
          // First sort by role priority
          const rolePriority = { admin: 1, teacher: 2, parent: 3, student: 4 };
          const aPriority = rolePriority[a.role] || 5;
          const bPriority = rolePriority[b.role] || 5;
          
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          
          // Then sort by display name or email
          const aName = a.displayName || a.email;
          const bName = b.displayName || b.email;
          return aName.localeCompare(bName);
        });
        
        setUsers(sortedUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(t('users.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [userData, t]);

  // Add new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!newUser.email || !newUser.password) {
      setError(t('validation.emailAndPasswordRequired'));
      return;
    }
    
    if (newUser.password !== newUser.confirmPassword) {
      setError(t('validation.passwordsDoNotMatch'));
      return;
    }
    
    try {
      setLoading(true);
      const auth = getAuth();
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );
      
      const { uid } = userCredential.user;
      
      // Create user in Firestore
      const userData: User = {
        uid,
        email: newUser.email,
        role: newUser.role,
        displayName: newUser.displayName || undefined,
        createdAt: new Date().toISOString(),
        subscription: newUser.subscription,
        subscriptionExpiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
      };
      
      await setDoc(doc(db, 'users', uid), userData);
      
      // Add user to local state
      setUsers(prevUsers => [...prevUsers, userData]);
      
      // Reset form
      setNewUser({
        email: '',
        password: '',
        confirmPassword: '',
        displayName: '',
        role: 'student',
        subscription: 'basic'
      });
      
      setIsAddingUser(false);
    } catch (err: any) {
      console.error('Error adding user:', err);
      setError(`${t('users.addError')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a user
  const handleStartEdit = (user: User) => {
    setEditingUserId(user.uid);
    setEditUser({
      displayName: user.displayName || '',
      role: user.role,
      subscription: user.subscription || 'basic'
    });
    setIsEditingUser(true);
  };
  
  // Update user
  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUserId) return;
    
    try {
      setLoading(true);
      
      // Update user in Firestore
      const userRef = doc(db, 'users', editingUserId);
      await updateDoc(userRef, {
        displayName: editUser.displayName || undefined,
        role: editUser.role,
        subscription: editUser.subscription
      });
      
      // Update user in local state
      setUsers(prevUsers => prevUsers.map(user => {
        if (user.uid === editingUserId) {
          return {
            ...user,
            displayName: editUser.displayName || undefined,
            role: editUser.role,
            subscription: editUser.subscription
          };
        }
        return user;
      }));
      
      setIsEditingUser(false);
      setEditingUserId(null);
    } catch (err) {
      console.error('Error updating user:', err);
      setError(t('users.updateError'));
    } finally {
      setLoading(false);
    }
  };
  
  // Delete user
  const handleDeleteUser = async (uid: string) => {
    // Check if user is super admin
    const user = users.find(u => u.uid === uid);
    
    if (user?.isSuperAdmin) {
      setError(t('users.cannotDeleteSuperAdmin'));
      return;
    }
    
    if (!window.confirm(t('users.confirmDelete'))) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete user from Firestore
      await deleteDoc(doc(db, 'users', uid));
      
      // Note: Deleting the actual Auth user requires an Admin SDK or a server function
      // We would typically call a Cloud Function here, but for now we'll just update the UI
      
      // Remove user from local state
      setUsers(prevUsers => prevUsers.filter(user => user.uid !== uid));
      
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(t('users.deleteError'));
    } finally {
      setLoading(false);
    }
  };
  
  // Filter users based on search and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = (
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    const matchesRole = selectedRole === 'All' || user.role === selectedRole;
    
    return matchesSearch && matchesRole;
  });
  
  // Get role label
  const getRoleLabel = (role: UserRole) => {
    return t(`roles.${role}`);
  };
  
  // Get subscription label
  const getSubscriptionLabel = (subscriptionId: string) => {
    const subscription = subscriptionOptions.find(s => s.id === subscriptionId);
    return subscription ? subscription.name : subscriptionId;
  };
  
  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Display loading state
  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#309D97]"></div>
      </div>
    );
  }
  
  // Display access denied message if not admin
  if (userData?.role !== 'admin') {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {t('errors.noPermission')}
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('users.management')}</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            className="float-right"
            onClick={() => setError('')}
          >
            &times;
          </button>
        </div>
      )}
      
      {/* Add User Form */}
      {isAddingUser && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-medium mb-4">{t('users.addUser')}</h2>
          <form onSubmit={handleAddUser}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}*</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.displayName')}</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({...newUser, displayName: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.password')}*</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.confirmPassword')}*</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newUser.confirmPassword}
                  onChange={(e) => setNewUser({...newUser, confirmPassword: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.role')}*</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value as UserRole})}
                  required
                >
                  <option value="student">{t('roles.student')}</option>
                  <option value="parent">{t('roles.parent')}</option>
                  <option value="teacher">{t('roles.teacher')}</option>
                  <option value="admin">{t('roles.admin')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.subscription')}</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newUser.subscription}
                  onChange={(e) => setNewUser({...newUser, subscription: e.target.value})}
                >
                  {subscriptionOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setIsAddingUser(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#309D97] text-white rounded-md hover:bg-opacity-90"
                disabled={loading}
              >
                {loading ? t('users.adding') : t('common.add')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Edit User Form */}
      {isEditingUser && editingUserId && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-medium mb-4">{t('users.editUser')}</h2>
          <form onSubmit={handleUpdateUser}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.displayName')}</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={editUser.displayName}
                  onChange={(e) => setEditUser({...editUser, displayName: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.role')}</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={editUser.role}
                  onChange={(e) => setEditUser({...editUser, role: e.target.value as UserRole})}
                  required
                >
                  <option value="student">{t('roles.student')}</option>
                  <option value="parent">{t('roles.parent')}</option>
                  <option value="teacher">{t('roles.teacher')}</option>
                  <option value="admin">{t('roles.admin')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.subscription')}</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={editUser.subscription}
                  onChange={(e) => setEditUser({...editUser, subscription: e.target.value})}
                >
                  {subscriptionOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setIsEditingUser(false);
                  setEditingUserId(null);
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#309D97] text-white rounded-md hover:bg-opacity-90"
                disabled={loading}
              >
                {loading ? t('users.updating') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Filters and search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.search')}</label>
            <input
              type="text"
              placeholder={t('users.searchPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.role')}</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="All">{t('users.allRoles')}</option>
              <option value="admin">{t('roles.admin')}</option>
              <option value="teacher">{t('roles.teacher')}</option>
              <option value="parent">{t('roles.parent')}</option>
              <option value="student">{t('roles.student')}</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {t('users.showing', { filtered: filteredUsers.length, total: users.length })}
          </div>
          
          <button 
            className="bg-[#309D97] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
            onClick={() => setIsAddingUser(!isAddingUser)}
          >
            {isAddingUser ? t('common.cancel') : t('users.addUser')}
          </button>
        </div>
      </div>
      
      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('users.userInfo')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.role')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('users.subscription')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('users.created')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr key={user.uid} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#309D97] bg-opacity-20 flex items-center justify-center text-[#309D97] font-medium">
                      {(user.displayName?.[0] || user.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.displayName || t('users.noName')}
                        {user.isSuperAdmin && 
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {t('users.superAdmin')}
                          </span>
                        }
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                    user.role === 'teacher' ? 'bg-blue-100 text-blue-800' :
                    user.role === 'parent' ? 'bg-green-100 text-green-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{getSubscriptionLabel(user.subscription || 'basic')}</div>
                  {user.subscriptionExpiry && (
                    <div className="text-xs text-gray-500">
                      {t('users.expiresOn', { date: formatDate(user.subscriptionExpiry) })}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(user.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    className="text-[#309D97] hover:text-[#1e7d77] mr-3"
                    onClick={() => handleStartEdit(user)}
                  >
                    {t('common.edit')}
                  </button>
                  {!user.isSuperAdmin && (
                    <button 
                      className="text-red-600 hover:text-red-800"
                      onClick={() => handleDeleteUser(user.uid)}
                      disabled={loading}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  {users.length === 0 
                    ? t('users.noUsers') 
                    : t('users.noMatchingUsers')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users; 